import React, { useEffect, useState } from 'react';
import { Terminal } from './Terminal';
import { PredictionResult } from './PredictionResult';
import { ShieldAlertIcon, ZapIcon, ScanSearchIcon } from 'lucide-react';
import { motion } from 'framer-motion';
import { BubbleButton } from './BubbleButton';
export const WingoPrediction = () => {
  const [periodNumber, setPeriodNumber] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [prediction, setPrediction] = useState<{
    sizeResult: string;
    colorResult: string;
  } | null>(null);
  const [processingMessages, setProcessingMessages] = useState<string[]>([]);
  const generatePrediction = () => {
    if (periodNumber.length !== 3 || isNaN(Number(periodNumber))) {
      return;
    }
    setIsProcessing(true);
    setPrediction(null);
    // Reset processing messages
    setProcessingMessages([]);
    // Simulate processing with messages
    const messages = ['Initializing prediction algorithm...', 'Accessing Wingo database...', 'Analyzing historical patterns...', 'Decrypting sequence variables...', 'Running quantum prediction model...', 'Calculating probability matrix...', 'Applying machine learning filters...', 'Finalizing prediction results...'];
    let i = 0;
    const interval = setInterval(() => {
      if (i < messages.length) {
        setProcessingMessages(prev => [...prev, messages[i]]);
        i++;
      } else {
        clearInterval(interval);
        // Generate consistent prediction based on period number
        const seed = Number(periodNumber);
        const sizeOptions = ['BIG', 'SMALL'];
        const colorOptions = ['RED', 'GREEN'];
        // Use simple deterministic algorithm based on the period number
        // Sum of digits for size, last digit for color
        const digits = periodNumber.split('').map(Number);
        const sum = digits.reduce((a, b) => a + b, 0);
        const lastDigit = digits[2];
        // Consistent mapping: even sum = BIG, odd sum = SMALL
        const sizeIndex = sum % 2 === 0 ? 0 : 1;
        // Consistent mapping: last digit even = RED, odd = GREEN
        const colorIndex = lastDigit % 2 === 0 ? 0 : 1;
        setPrediction({
          sizeResult: sizeOptions[sizeIndex],
          colorResult: colorOptions[colorIndex]
        });
        setIsProcessing(false);
      }
    }, 400);
  };
  // Reset prediction when period number changes
  useEffect(() => {
    setPrediction(null);
  }, [periodNumber]);
  return <motion.div className="w-full max-w-md" initial={{
    opacity: 0,
    y: 20
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    duration: 0.5
  }} data-id="element-91">
      <motion.div className="border border-green-500 p-4 rounded-lg bg-black shadow-lg animate-glow grid-bg" whileHover={{
      boxShadow: '0 0 25px rgba(72, 255, 128, 0.5)'
    }} transition={{
      duration: 0.3
    }} data-id="element-92">
        <div className="flex items-center justify-between mb-4 border-b border-green-500 pb-2" data-id="element-93">
          <motion.div className="flex items-center" whileHover={{
          scale: 1.03
        }} transition={{
          type: 'spring',
          stiffness: 400,
          damping: 10
        }} data-id="element-94">
            <ShieldAlertIcon className="text-green-400 mr-2 animate-pulse-custom" size={20} data-id="element-95" />
            <h1 className="text-xl font-mono text-green-400" data-id="element-96">
              WINGO PREDICTOR v1.0
            </h1>
          </motion.div>
          <div className="flex space-x-2" data-id="element-97">
            <motion.div className="w-3 h-3 rounded-full bg-red-500" whileHover={{
            scale: 1.2
          }} data-id="element-98"></motion.div>
            <motion.div className="w-3 h-3 rounded-full bg-yellow-500" whileHover={{
            scale: 1.2
          }} data-id="element-99"></motion.div>
            <motion.div className="w-3 h-3 rounded-full bg-green-500" whileHover={{
            scale: 1.2
          }} data-id="element-100"></motion.div>
          </div>
        </div>
        <div className="mb-4" data-id="element-101">
          <label className="block text-green-400 font-mono text-sm mb-1" data-id="element-102">
            ENTER LAST 3 DIGITS OF PERIOD NUMBER:
          </label>
          <div className="flex" data-id="element-103">
            <motion.input type="text" value={periodNumber} onChange={e => {
            const value = e.target.value;
            if (value === '' || /^\d{0,3}$/.test(value)) {
              setPeriodNumber(value);
            }
          }} className="bg-black border border-green-500 text-green-400 font-mono p-2 w-full focus:outline-none terminal-glow" maxLength={3} placeholder="000" whileFocus={{
            boxShadow: '0 0 15px rgba(72, 255, 128, 0.7)'
          }} data-id="element-104" />
            <BubbleButton onClick={generatePrediction} disabled={periodNumber.length !== 3 || isProcessing} className="ml-2 rounded" color="green" data-id="element-105">
              <ZapIcon size={18} className="mr-1" data-id="element-106" />
              HACK
            </BubbleButton>
          </div>
        </div>
        {isProcessing && <Terminal messages={processingMessages} data-id="element-107" />}
        {prediction && !isProcessing && <PredictionResult prediction={prediction} periodNumber={periodNumber} data-id="element-108" />}
        <motion.div className="mt-4 border-t border-green-500/30 pt-2" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        delay: 0.5
      }} data-id="element-109">
          <div className="flex items-center text-xs text-green-500/70 font-mono" data-id="element-110">
            <ScanSearchIcon size={14} className="mr-1 animate-pulse-custom" data-id="element-111" />
            <span className="animate-scan" data-id="element-112">SECURE CONNECTION ESTABLISHED</span>
          </div>
        </motion.div>
      </motion.div>
    </motion.div>;
};